import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { exportXlsxAoA } from "@/lib/xlsx";

export default function ImportDialog({ open, onClose, onDone }) {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setPreview(null); };

  const downloadTemplate = () => {
    const headers = ["RUT", "Nombre", "Certificado de Antecedentes", "Ficha", "Cédula",
      "Reglamento de Transporte", "Registro de Datos personales", "Exámenes médicos", "Otros Exámenes", "Contrato"];
    const sample = ["12345678-9", "Cristian Rojas", "Completado", "Pendiente", "Completado",
      "En progreso", "Pendiente", "Completado", "Pendiente", "Pendiente"];
    exportXlsxAoA([headers, sample], "planilla_base_trabajadores", "Trabajadores");
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f); setLoading(true); setPreview(null);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo leer el archivo"); reset(); }
    finally { setLoading(false); }
  };

  const execute = async () => {
    setLoading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/imports/execute", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importación completa: ${data.new} nuevos, ${data.updated} actualizados`);
      onDone && onDone(); reset(); onClose();
    } catch (e) { toast.error("No se pudo ejecutar la importación"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Upload size={18} className="text-blue-600" /> Importar trabajadores (Excel / CSV)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Solo estados de cumplimiento — no se almacenan documentos. Columnas: RUT, Nombre y una columna por documento (Certificado de Antecedentes, Ficha, Cédula, etc.) con valores como Completado / En progreso / Pendiente.</p>

        <button data-testid="download-template-button" onClick={downloadTemplate} className="inline-flex items-center gap-2 self-start text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg px-3 py-1.5">
          <FileSpreadsheet size={15} /> Descargar planilla base (CSV)
        </button>

        {!preview && (
          <button
            data-testid="import-dropzone"
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-slate-200 rounded-xl py-10 flex flex-col items-center gap-2 text-slate-400 hover:border-blue-300 hover:text-blue-500 transition-colors"
          >
            {loading ? <Loader2 className="animate-spin" /> : <FileSpreadsheet size={32} />}
            <span className="text-sm font-medium">{file ? file.name : "Haz clic para seleccionar un archivo .xlsx o .csv"}</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="import-file-input" />

        {preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <Stat label="Registros" value={preview.total} />
              <Stat label="Nuevos" value={preview.new} color="text-blue-600" />
              <Stat label="Actualizados" value={preview.updated} color="text-emerald-600" />
              <Stat label="Errores" value={preview.errors.length} color="text-red-600" />
            </div>
            <div className="max-h-52 overflow-y-auto border border-slate-100 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr><th className="text-left p-2">RUT</th><th className="text-left p-2">Nombre</th><th className="text-left p-2">Acción</th></tr></thead>
                <tbody>
                  {preview.preview.map((r, i) => (
                    <tr key={i} className="border-t border-slate-50">
                      <td className="p-2 font-mono">{r.rut || "—"}</td><td className="p-2">{r.name || "—"}</td>
                      <td className="p-2">{r.action === "new" ? <span className="text-blue-600 flex items-center gap-1"><CheckCircle2 size={12} /> Nuevo</span> : <span className="text-emerald-600">Actualizar</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.errors.length > 0 && (
              <div className="border border-red-100 bg-red-50/40 rounded-lg">
                <div className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-700 border-b border-red-100">
                  <AlertCircle size={13} /> {preview.errors.length} fila(s) con errores — se omitirán al importar
                </div>
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-red-50 text-red-600"><tr><th className="text-left p-2 w-12">Fila</th><th className="text-left p-2">RUT</th><th className="text-left p-2">Motivo</th></tr></thead>
                    <tbody>
                      {preview.errors.map((er, i) => (
                        <tr key={i} className="border-t border-red-100">
                          <td className="p-2 font-mono text-red-700">{er.row ?? "—"}</td>
                          <td className="p-2 font-mono text-slate-700">{er.rut || "—"}</td>
                          <td className="p-2 text-slate-700">{er.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={reset} className="text-sm text-slate-500 px-4 py-2">Cambiar archivo</button>
              <button data-testid="import-execute-button" onClick={execute} disabled={loading} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
                {loading && <Loader2 size={15} className="animate-spin" />} Confirmar importación
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color = "text-slate-800" }) {
  return <div className="bg-slate-50 rounded-lg p-2.5 text-center border border-slate-100"><p className={`text-xl font-display font-bold ${color}`}>{value}</p><p className="text-[11px] text-slate-400">{label}</p></div>;
}
